/**
 * Vue Router config — hash history per D-009.
 *
 * Per D-008/D-027: paths forbliver engelske (tekniske artefakter), men
 * meta.title er på dansk fordi det vises i UI'et og browser-fanebladet.
 *
 * URL-struktur følger menu-hierarkiet (session 50, Charlie): hver sektion er
 * et top-level segment, fx /marketing, /inbound, /stores, /items, /logistics,
 * /accounting, /ops, /settings. Rent brud med de gamle /dashboards/*-stier
 * (ingen redirects) — en gammel URL rammer ingen route og giver et tomt
 * indhold-areal.
 *
 * ⚠ Fra [D-198] og frem gælder 12-conventions §21 for ALT andet: en slug der
 * har været i drift dør aldrig. Flytter en side, bliver den gamle sti stående
 * som redirect FOR ALTID, og grants kopieres med `migrate:page-slug-grants`
 * FØR deploy. Redirect-blokkene nederst i hver sektion herunder er dét arkiv.
 */

import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router';

import { auth, ensureSessionLoaded } from './composables/useAuth';
import {
  canAccessPath,
  fetchPermissions,
  firstAllowedPath,
  permissionsState,
} from './composables/usePermissions';
import {
  ANALYSES_MOVED_TO_CAMPAIGNS,
  ANALYSIS_LEGACY_SLUGS,
  analysisPagePath,
  CAMPAIGN_MENU,
  CAMPAIGNS_BASE_PATH,
  resolveAnalysisUid,
} from '@cockpit/shared';
import { watchLeaving } from './lib/query-patch';
import { consumeReturnAfterLogin } from './lib/return-after-login';
import { startPageLoad } from './lib/saving';
import type { RoleCode } from './permissions';

const placeholderView = () => import('./views/DashboardView.vue');

/**
 * `meta.empty` — siden er OPRETTET, men har intet indhold endnu (Charlie
 * 19-08-2026). Sidebaren tegner sådanne menupunkter i en svag grå, så man kan
 * se på menuen hvad der er bygget, uden at klikke sig frem til en tom side.
 *
 * Flaget sættes HER, ved ruten, og ingen andre steder: sidebaren læser det af
 * routeren. Bygges en side ud, fjernes flaget i samme ombæring som viewet
 * skiftes, og menuen retter sig selv. En liste i sidebaren ville drifte fra
 * routeren første gang nogen glemte det ene af de 2 steder.
 */

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('./views/LoginView.vue'),
    meta: { title: 'Log ind', public: true },
  },
  // [D-141]-review-fix (#8/#15): katalog-LØS landing for autentificerede brugere
  // uden nogen tilladt side. STÅR ALDRIG i side-kataloget og er `public`, så den
  // aldrig selv kan gates af permission-guarden → redirect-loopet er strukturelt
  // umuligt. Sender guarden hertil når firstAllowedPath er null.
  {
    path: '/no-access',
    name: 'no-access',
    component: () => import('./views/NoAccessView.vue'),
    meta: { title: 'Ingen adgang', public: true },
  },
  {
    path: '/',
    redirect: '/marketing/blended',
  },

  // ---- Marketing ----
  // [D-183] (17-08-2026): Performance er delt op i sektionens tværgående side +
  // én underside pr. kanal. Undersiderne deler header-filtrering via
  // MarketingFilters + useMarketingData.
  // [D-198] (17-08-2026, Charlie): den tværgående side heder nu **Blended** og
  // har fået sin egen slug '/marketing/blended' — navnet siger hvad tallene ER
  // (blendede/fler-kanal), hvor "Oversigt" kun sagde hvor de lå.
  {
    path: '/marketing/blended',
    name: 'marketing-blended',
    component: () => import('./views/marketing/BlendedView.vue'),
    meta: { title: 'Blended' },
  },
  // Gamle slugs → Blended (12-conventions §21: en slug der har været i drift
  // beholder ALTID en redirect, så delte links og bogmærker aldrig dør).
  { path: '/marketing', redirect: '/marketing/blended' },
  { path: '/marketing/performance', redirect: '/marketing/blended' },
  {
    path: '/marketing/paid-search',
    name: 'marketing-paid-search',
    component: () => import('./views/marketing/PaidChannelView.vue'),
    meta: { title: 'Paid search', channel: 'paid_search' },
  },
  {
    path: '/marketing/paid-social',
    name: 'marketing-paid-social',
    component: () => import('./views/marketing/PaidChannelView.vue'),
    meta: { title: 'Paid social', channel: 'paid_social' },
  },
  {
    path: '/marketing/organic-search',
    name: 'marketing-organic-search',
    component: () => import('./views/marketing/OrganicSearchView.vue'),
    meta: { title: 'Organic search' },
  },
  // Organic SoMe er tom af en DATA-årsag, ikke en prioritering: organisk
  // social er ~0 sessions i GA4 og foldes ind i "Øvrig" i kanal-modellen.
  {
    path: '/marketing/organic-social',
    name: 'marketing-organic-social',
    component: () => import('./views/marketing/MarketingEmptyView.vue'),
    meta: {
      title: 'Organic SoMe',
      empty: true,
      emptyNote:
        'Organisk social har p.t. ~0 sessions i GA4 og foldes derfor ind i kanalen "Øvrig" i attributions-modellen. Der er ingen selvstændig serie at vise endnu, kommer der en, bygges siden ud her.',
    },
  },
  // Overvågning ([D-383]): Organic SoMe-gruppens første underside — personer
  // (typisk offentligt kendte, fx influencers) som marketing følger tæt, med
  // hændelsesspor fra BC/Klaviyo og agent-forslag.
  {
    path: '/marketing/organic-social/monitoring',
    name: 'marketing-organic-social-monitoring',
    component: () => import('./views/marketing/OrganicSomeMonitoringView.vue'),
    meta: { title: 'Organic SoMe: Overvågning' },
  },
  // Email ([D-372]): Klaviyo-mails m. rapport-tal + eksempel-drawer.
  {
    path: '/marketing/email',
    name: 'marketing-email',
    component: () => import('./views/marketing/EmailView.vue'),
    meta: { title: 'Email' },
  },
  {
    path: '/marketing/sms',
    name: 'marketing-sms',
    component: () => import('./views/marketing/MarketingEmptyView.vue'),
    meta: { title: 'SMS', empty: true },
  },
  {
    path: '/marketing/print',
    name: 'marketing-print',
    component: () => import('./views/marketing/MarketingEmptyView.vue'),
    meta: { title: 'Print', empty: true },
  },
  {
    path: '/marketing/outdoor',
    name: 'marketing-outdoor',
    component: () => import('./views/marketing/MarketingEmptyView.vue'),
    meta: { title: 'Outdoor', empty: true },
  },
  // Diagnose er flyttet til egen hovedmenu Strategi ([D-223], 20-08-2026) —
  // diagnosen dækker hele forretningen, ikke kun marketing. §21: den gamle
  // slug redirecter for altid, og grants er migreret med page-slug-scriptet.
  { path: '/marketing/diagnostics', redirect: '/strategy/diagnosis' },
  // Spend-budget ([D-249]): kanal-budget mod platformens egne spend-tal.
  {
    path: '/marketing/spend-budget',
    name: 'marketing-spend-budget',
    component: () => import('./views/marketing/SpendBudgetView.vue'),
    meta: { title: 'Spend-budget' },
  },
  // Kampagner ([D-262], 31-08-2026): oversigt + ÉN SIDE pr. kampagne, samme
  // navigations-mønster som vækstcaserne — men UDEN deres grant-opdeling.
  // Kampagnesiden er parametriseret og har ingen katalog-side: adgang og
  // redigér-ret arves fra listen, fordi canAccessPath/canEditPath resolver en
  // sti uden egen katalog-side til nærmeste forælder, og api-authz mapper hele
  // '/api/campaigns' til den ene side. Søgeindekset springer den over
  // ([D-215]-mønstret: en ':id'-sti kan ikke navigeres til fra et søgehit).
  // [D-304] (18-09-2026): oversigten er delt i 5 undermenuer efter kampagnens
  // fase (Udkast, Godkendte, Planlagte, Aktive, Afsluttede). Undermenuerne er
  // NAVIGATION, ikke adgang (samme princip som analysernes kategorier, [D-289]):
  // de har ingen egne katalog-sider, så adgang og redigér-ret arves fra
  // '/marketing/campaigns'. Den gamle adresse lander på første undermenu (§21).
  // Ruterne står skrevet ud (ikke genereret af CAMPAIGN_MENU), fordi
  // søgeindeksets generator læser router.ts statisk. Boot-værnet nedenfor
  // fejler højlydt, hvis listen her og CAMPAIGN_MENU i shared drifter.
  { path: '/marketing/campaigns', redirect: '/marketing/campaigns/drafts' },
  {
    path: '/marketing/campaigns/drafts',
    name: 'marketing-campaigns-drafts',
    component: () => import('./views/marketing/KampagnerView.vue'),
    meta: { title: 'Kampagner: Udkast', campaignLifecycle: 'draft' },
  },
  {
    path: '/marketing/campaigns/approved',
    name: 'marketing-campaigns-approved',
    component: () => import('./views/marketing/KampagnerView.vue'),
    meta: { title: 'Kampagner: Godkendte', campaignLifecycle: 'approved' },
  },
  {
    path: '/marketing/campaigns/planned',
    name: 'marketing-campaigns-planned',
    component: () => import('./views/marketing/KampagnerView.vue'),
    meta: { title: 'Kampagner: Planlagte', campaignLifecycle: 'planned' },
  },
  {
    path: '/marketing/campaigns/active',
    name: 'marketing-campaigns-active',
    component: () => import('./views/marketing/KampagnerView.vue'),
    meta: { title: 'Kampagner: Aktive', campaignLifecycle: 'active' },
  },
  {
    path: '/marketing/campaigns/ended',
    name: 'marketing-campaigns-ended',
    component: () => import('./views/marketing/KampagnerView.vue'),
    meta: { title: 'Kampagner: Afsluttede', campaignLifecycle: 'ended' },
  },
  // Kampagne-dashboards ([D-420], Charlie 20-10-2026): listen over aktive og
  // øvrige kampagner ±30 dage, og en MONITOR-visning pr. kampagne uden menu.
  // Navigation under Kampagner som faserne: ingen katalog-side, grantet på
  // '/marketing/campaigns' arves. Monitoren har meta.monitor: den tegnes uden
  // app-skal og må åbnes uden app-session, fordi den bærer sit eget login på
  // 90 dage (auth/dashboard-session.ts på serveren). Al adgang afgøres af
  // serveren pr. kald; uden gyldigt login viser siden kun en Log ind-knap.
  {
    path: '/marketing/campaigns/dashboards',
    name: 'marketing-campaigns-dashboards',
    component: () => import('./views/marketing/KampagneDashboardsView.vue'),
    meta: { title: 'Kampagner: Dashboards' },
  },
  {
    path: '/marketing/campaigns/dashboards/:id',
    name: 'marketing-campaign-dashboard',
    component: () => import('./views/marketing/KampagneDashboardView.vue'),
    meta: { title: 'Kampagne-dashboard', monitor: true },
  },
  {
    // [D-267], Charlie 01-09-2026: ':id' er kampagnens public_id (32 hex-tegn),
    // ikke rækkens nummer. Den numeriske form er BEVIDST ikke længere en vej
    // ind — hverken direkte eller som redirect. Hele bestillingen er, at man
    // ikke skal kunne skrive 1 i stedet for 2 og lande på nabokampagnen, og et
    // "venligt" redirect ville lade præcis den dør stå åben. Den kortlivede
    // '?kampagne=<n>'-form fra drawer-udgaven er væk af samme grund; modulet er
    // 1 døgn gammelt, så der er ingen delte links at redde (§21-afvejningen).
    path: '/marketing/campaigns/:id',
    name: 'marketing-campaign',
    component: () => import('./views/marketing/KampagneView.vue'),
    meta: { title: 'Kampagne' },
  },
  {
    // [D-459]: kampagnebriefen til salget, en A4-one-pager der gemmes som PDF.
    // meta.bare: tegnes uden app-skal (siden ER arket), men kræver login og
    // grant som kampagnesiden; adgangen arves fra '/marketing/campaigns'.
    path: '/marketing/campaigns/:id/brief',
    name: 'marketing-campaign-brief',
    component: () => import('./views/marketing/KampagneBriefView.vue'),
    meta: { title: 'Kampagnebrief', bare: true },
  },
  {
    path: '/marketing/retainers',
    name: 'marketing-retainers',
    component: () => import('./views/marketing/RetainereView.vue'),
    meta: { title: 'Retainere' },
  },
  {
    path: '/marketing/qr-codes',
    name: 'marketing-qr-codes',
    component: () => import('./views/marketing/QrCodesView.vue'),
    meta: { title: 'QR-koder' },
  },
  // Månedstal ([D-335], Charlie 28-09-2026): YoY, MTM og faktiske tal for de 6
  // nøgletal (ad spend, besøg, sessions, omsætning, bruttofortjeneste, resultat
  // før skat), ét år ad gangen i filter-draweren. Øverst i Marketing-menuen.
  {
    path: '/marketing/monthly',
    name: 'marketing-monthly',
    component: () => import('./views/marketing/MaanedstalView.vue'),
    meta: { title: 'Månedstal' },
  },
  // Index ([D-252]): marketing-nøgletallene måned for måned, ét driftår pr.
  // kolonne. Søstersiden til Regnskab → Index; spendet kommer fra
  // BC-kontoplanen, fordi platformene ikke rækker 13 år tilbage.
  {
    path: '/marketing/index',
    name: 'marketing-index',
    component: () => import('./views/marketing/MarketingIndexView.vue'),
    meta: { title: 'Marketing index' },
  },
  // Customer Match ([D-285]): kundelister + butikskøb som offline-konverteringer
  // fra BC til Google Ads og Meta. Egen underside (Charlie 08-09-2026, §48).
  {
    path: '/marketing/customer-match',
    name: 'marketing-customer-match',
    component: () => import('./views/marketing/CustomerMatchView.vue'),
    meta: { title: 'Customer Match' },
  },
  // Konkurrenter ([D-292]): listen + én detaljeside pr. konkurrent (§48: egen
  // rute, ikke drawer). Detaljen arver adgang og indstillinger fra listen.
  {
    path: '/marketing/competitors',
    name: 'marketing-competitors',
    component: () => import('./views/marketing/CompetitorsView.vue'),
    meta: { title: 'Konkurrenter' },
  },
  {
    path: '/marketing/competitors/:id',
    name: 'marketing-competitor-detail',
    component: () => import('./views/marketing/CompetitorDetailView.vue'),
    meta: { title: 'Konkurrent' },
  },
  // Affiliates ([D-349]): menugruppe uden egen side; hver partner er sin egen
  // side med sit eget grant. Gruppens adresse lander på første partner.
  { path: '/marketing/affiliates', redirect: '/marketing/affiliates/loyalty-key' },
  {
    path: '/marketing/affiliates/loyalty-key',
    name: 'marketing-affiliates-loyalty-key',
    component: () => import('./views/marketing/LoyaltyKeyView.vue'),
    meta: { title: 'Loyalty Key' },
  },
  // Brand guidelines ([D-425], 21-10-2026): Bad & Flisers CVI som undersider.
  // 1 katalog-side ('/marketing/brand'); undersiderne arver grantet (prefix).
  { path: '/marketing/brand', redirect: '/marketing/brand/overview' },
  {
    path: '/marketing/brand/overview',
    name: 'marketing-brand-overview',
    component: () => import('./views/marketing/brand/BrandOverviewView.vue'),
    meta: { title: 'Brand guidelines' },
  },
  {
    path: '/marketing/brand/visual-identity',
    name: 'marketing-brand-visual-identity',
    component: () => import('./views/marketing/brand/BrandVisualIdentityView.vue'),
    meta: { title: 'Brand guidelines · Visuel identitet' },
  },
  {
    path: '/marketing/brand/tone-of-voice',
    name: 'marketing-brand-tone-of-voice',
    component: () => import('./views/marketing/brand/BrandToneOfVoiceView.vue'),
    meta: { title: 'Brand guidelines · Tone of voice' },
  },
  {
    path: '/marketing/brand/core-story',
    name: 'marketing-brand-core-story',
    component: () => import('./views/marketing/brand/BrandCoreStoryView.vue'),
    meta: { title: 'Brand guidelines · Kernefortælling' },
  },
  {
    path: '/marketing/brand/code-of-conduct',
    name: 'marketing-brand-code-of-conduct',
    component: () => import('./views/marketing/brand/BrandCodeOfConductView.vue'),
    meta: { title: 'Brand guidelines · Code of conduct' },
  },
  // Arkivet er flyttet til egen hovedmenu ([D-215]) og omdøbt til Analyser
  // ([D-276]) — se blokken nedenfor.
  // 12-conventions §21: en slug der har været i drift dør aldrig. Den ældste
  // side bar casen som '?case=<id>', så redirect'en tager id'et med over, når
  // det er der; ellers lander man på oversigten.
  // ⚠ §21b: begge gamle stier peger DIREKTE på '/analyses', aldrig i kæde
  // gennem hinanden. En redirect der rammer en anden redirect er en fælde:
  // fjernes det midterste led senere, dør det yderste tavst.
  // [D-289]: analyser bor på /analyses/<uid>; de gamle slugs oversættes med
  // resolveAnalysisUid (kendt slug → uid, uid → uid, alt andet → oversigten),
  // så ingen af de 3 pensionerede former ender på en side der ikke findes.
  {
    path: '/marketing/growth-cases',
    redirect: (to) => {
      const id = typeof to.query.case === 'string' ? to.query.case : '';
      // `case` fjernes fra query'en: den er blevet til en STI, og en efterladt
      // parameter i et delt link ville se ud som om den stadig betyder noget.
      const { case: _dropped, ...query } = to.query;
      const uid = resolveAnalysisUid(id);
      return uid ? { path: analysisPagePath(uid), query } : { path: '/analyses', query };
    },
  },
  // [D-276]: '/growth-cases' har været i drift siden 19-08-2026 og er delt
  // eksternt (Bonzer-casen med bureauet, Loyalty Key-casen med leverandøren),
  // så begge niveauer skal overleve navneskiftet.
  { path: '/growth-cases', redirect: (to) => ({ path: '/analyses', query: to.query }) },
  {
    path: '/growth-cases/:id',
    redirect: (to) => {
      const uid = resolveAnalysisUid(String(to.params.id ?? ''));
      return uid ? { path: analysisPagePath(uid), query: to.query } : { path: '/analyses', query: to.query };
    },
  },
  // [D-289] (11-09-2026): de slugs der var i drift på /analyses/<slug> fra
  // [D-276] til UID-skiftet (delt eksternt: Bonzer, Morningtrain, Adtimize)
  // redirecter DIREKTE til UID-stien (§21b: aldrig i kæde). Listen er lukket:
  // nye analyser får aldrig en slug-sti, og et uid man ikke må se er 404 i
  // API'et uanset hvad man skriver i adresselinjen.
  {
    path: `/analyses/:slug(${Object.keys(ANALYSIS_LEGACY_SLUGS).join('|')})`,
    redirect: (to) => ({
      path: analysisPagePath(ANALYSIS_LEGACY_SLUGS[String(to.params.slug)] ?? ''),
      query: to.query,
    }),
  },

  // [D-355] (01-10-2026): en analyse der er flyttet ind på en kampagnes side
  // (Black November 2026: planen, tidslinjen og prognosen står nu på kampagnen)
  // sendes DIREKTE videre til kampagnen (§21/§21b). Ruten er statisk og
  // vinder derfor over /analyses/:id.
  ...Object.entries(ANALYSES_MOVED_TO_CAMPAIGNS).map(([uid, publicId]): RouteRecordRaw => ({
    path: `/analyses/${uid}`,
    redirect: (to) => ({ path: `/marketing/campaigns/${publicId}`, query: to.query }),
  })),

  // ---- Analyser ([D-215], 19-08-2026; omdøbt fra Vækstcases [D-276], 03-09-2026) ----
  // EGEN hovedmenu med én side pr. analyse. Opdelingen er en ADGANGS-beslutning,
  // ikke en navigations-beslutning: adgang er per-side og fail-closed
  // ([D-141]), så en analyse kun kan deles med den leverandør den handler om,
  // hvis analysen ER en side. Oversigten er sin egen side med sit eget grant —
  // man kan have én analyse uden at kunne se listen, og omvendt.
  {
    path: '/analyses',
    name: 'growth-cases',
    component: () => import('./views/growth-cases/GrowthCasesListView.vue'),
    meta: { title: 'Analyser' },
  },
  {
    // [D-289]: ':id' er analysens uid (32 hex-tegn, frosset i casen), ikke en
    // slug. Ruten er bevidst IKKE begrænset med et regex: en forkert adresse
    // skal give appens egen "Ukendt analyse"-besked fra API'et (404), ikke en
    // tom side. Adgangen afgøres af grantet på /analyses/<uid> (fail-closed).
    path: '/analyses/:id',
    name: 'growth-case',
    component: () => import('./views/growth-cases/GrowthCaseView.vue'),
    meta: { title: 'Analyse' },
  },

  // ---- Strategi ([D-223], 20-08-2026) ----
  // Diagnose-agentens side: seneste diagnose øverst, historikken som
  // åbn/luk-kasser nederst. Undersiden pr. rapport er parametriseret og
  // hører under SAMME grant som oversigten (den er ikke en katalogside) —
  // søgeindekset springer parametriserede ruter over ([D-215]-mønstret).
  {
    path: '/strategy/diagnosis',
    name: 'strategy-diagnosis',
    component: () => import('./views/strategy/DiagnoseView.vue'),
    meta: { title: 'Diagnose' },
  },
  {
    path: '/strategy/diagnosis/:id',
    name: 'strategy-diagnosis-report',
    component: () => import('./views/strategy/DiagnoseReportView.vue'),
    meta: { title: 'Diagnose-rapport' },
  },
  // Præsentationer (26-10-2026): levende dæk til storskærm, første emne er
  // Ledelsesmøder (månedsmødets fælles overblik). [D-534]: hver type og hvert
  // dæk er sin egen katalogside, og adgangen arves nedad (Alle, type, dæk;
  // presentationAccess i shared), så guarden lukker et dæk uden adgang
  // (søgeindekset springer parametriserede ruter over).
  {
    path: '/strategy/presentations',
    name: 'strategy-presentations',
    component: () => import('./views/strategy/presentations/PresentationsView.vue'),
    meta: { title: 'Præsentationer' },
  },
  // [D-445] pkt 28: det gamle dato-id (fx 2026-10-26) viderestiller for altid
  // til månedsnøglen (2026-10); et gammelt ?slide=N bliver til stien /N, og
  // resten af query'en følger med (§21).
  {
    path: '/strategy/presentations/management/:id(\\d{4}-\\d{2}-\\d{2})',
    redirect: (to) => {
      const base = `/strategy/presentations/management/${String(to.params.id).slice(0, 7)}`;
      const slide = Number(to.query.slide);
      const query = { ...to.query };
      delete query.slide;
      return { path: Number.isInteger(slide) && slide > 1 ? `${base}/${slide}` : base, query };
    },
  },
  // Ledelses-dækket FØR den generiske emne-rute, så 'management/:id' vinder.
  // Slide-nummeret er en valgfri sti-del: /management/2026-10/3 (slide 1 = ren sti).
  {
    path: '/strategy/presentations/management/:id/:slide(\\d+)?',
    name: 'strategy-presentation-management-deck',
    component: () => import('./views/strategy/presentations/ManagementDeckView.vue'),
    meta: { title: 'Præsentation' },
  },
  // [D-510] (01-11-2026): det gamle kampagnedæk er slettet; adressen peger for
  // altid på kampagnens ugestatus til ledelsen, som afløste det (§21).
  {
    path: '/strategy/presentations/campaigns/black-november-2026/:slide(\\d+)?',
    redirect: (to) => ({ path: '/strategy/presentations/campaigns/black-november-2026-status', query: to.query }),
  },
  // Ugedæk (Charlie 01-11-2026, [D-510]): samme dæk-komponent, kampagnens ugetal.
  // Ledelsens dæk under Kampagner, bureauets under Bureaumøder.
  {
    path: '/strategy/presentations/campaigns/:id/:slide(\\d+)?',
    name: 'strategy-presentation-campaign-deck',
    component: () => import('./views/strategy/presentations/ManagementDeckView.vue'),
    meta: { title: 'Præsentation', deckTopic: 'campaigns' },
  },
  {
    path: '/strategy/presentations/agency/:id/:slide(\\d+)?',
    name: 'strategy-presentation-agency-deck',
    component: () => import('./views/strategy/presentations/ManagementDeckView.vue'),
    meta: { title: 'Præsentation', deckTopic: 'agency' },
  },
  {
    path: '/strategy/presentations/:topic',
    name: 'strategy-presentations-topic',
    component: () => import('./views/strategy/presentations/PresentationTopicView.vue'),
    meta: { title: 'Præsentationer' },
  },

  // ---- Inbound ----
  // [D-336] engelske stier; /inbound/telefon viderestiller for altid (§21).
  {
    path: '/inbound/phone',
    name: 'inbound-phone',
    component: () => import('./views/inbound/TelefonView.vue'),
    meta: { title: 'Telefon' },
  },
  { path: '/inbound/telefon', redirect: (to) => ({ path: '/inbound/phone', query: to.query }) },
  {
    path: '/inbound/store-visits',
    name: 'inbound-store-visits',
    component: () => import('./views/inbound/ButiksbesogView.vue'),
    meta: { title: 'Butiksbesøg' },
  },
  {
    path: '/inbound/inquiries',
    name: 'inbound-inquiries',
    component: () => import('./views/inbound/HenvendelserView.vue'),
    meta: { title: 'Henvendelser' },
  },
  {
    path: '/inbound/bookings',
    name: 'inbound-bookings',
    component: () => import('./views/inbound/BookingerView.vue'),
    meta: { title: 'Bookinger' },
  },
  // Leads ([D-371]): lead-oversigten på tværs af kanaler + Klaviyo-status.
  {
    path: '/inbound/leads',
    name: 'inbound-leads',
    component: () => import('./views/inbound/LeadsView.vue'),
    meta: { title: 'Leads' },
  },
  {
    path: '/inbound/competitions',
    name: 'inbound-competitions',
    component: placeholderView,
    meta: { title: 'Konkurrencer', empty: true },
  },

  // ---- Webshop ----
  // [D-226] (24-08-2026): SEO-motoren — Webshop → SEO → 5 undersider.
  // Placeholder-siden '/webshop' er UDGÅET; slug'en var i drift (grant-bar
  // side siden [D-141]) og beholder derfor en redirect for altid (§21), og
  // grants migreres med migrate:page-slug-grants FØR deploy.
  { path: '/webshop', redirect: '/webshop/seo/review' },
  { path: '/webshop/seo', redirect: '/webshop/seo/review' },
  {
    path: '/webshop/seo/review',
    name: 'webshop-seo-review',
    component: () => import('./views/webshop/SeoReviewView.vue'),
    meta: { title: 'SEO · Til gennemgang' },
  },
  {
    path: '/webshop/seo/products',
    name: 'webshop-seo-products',
    component: () => import('./views/webshop/SeoProductsView.vue'),
    meta: { title: 'SEO · Produkter' },
  },
  {
    path: '/webshop/seo/categories',
    name: 'webshop-seo-categories',
    component: () => import('./views/webshop/SeoCategoriesView.vue'),
    meta: { title: 'SEO · Kategorier' },
  },
  {
    path: '/webshop/seo/pages',
    name: 'webshop-seo-pages',
    component: () => import('./views/webshop/SeoPagesView.vue'),
    meta: { title: 'SEO · Sider' },
  },
  {
    path: '/webshop/seo/keywords',
    name: 'webshop-seo-keywords',
    component: () => import('./views/webshop/SeoKeywordsView.vue'),
    meta: { title: 'SEO · Søgeord' },
  },
  // Regelsæt ([D-233]): det levende regelsæt (kladde → prøve-render → aktivér).
  {
    path: '/webshop/seo/rules',
    name: 'webshop-seo-rules',
    component: () => import('./views/webshop/SeoRulesView.vue'),
    meta: { title: 'SEO · Regelsæt' },
  },
  // Menuer ([D-242]): sitets navigation m. menu-tjekket (kun læsning).
  {
    path: '/webshop/seo/menus',
    name: 'webshop-seo-menus',
    component: () => import('./views/webshop/SeoMenusView.vue'),
    meta: { title: 'SEO · Menuer' },
  },
  // Redirects ([D-319]): kandidater til omdirigering/nedlæggelse.
  {
    path: '/webshop/seo/redirects',
    name: 'webshop-seo-redirects',
    component: () => import('./views/webshop/SeoRedirectsView.vue'),
    meta: { title: 'SEO · Redirects' },
  },
  // Log ([D-320]): indholdsændringer skrevet til shoppen + målt effekt.
  {
    path: '/webshop/seo/log',
    name: 'webshop-seo-log',
    component: () => import('./views/webshop/SeoLogView.vue'),
    meta: { title: 'SEO · Log' },
  },
  // CRO ([D-241]): konverterings-sporet — Webshops 2. niveau 3-gruppe.
  { path: '/webshop/cro', redirect: '/webshop/cro/conversion' },
  {
    path: '/webshop/cro/conversion',
    name: 'webshop-cro-conversion',
    component: () => import('./views/webshop/CroConversionView.vue'),
    meta: { title: 'CRO · Konvertering' },
  },
  {
    // [D-310]: én PageSpeed-måling på sin egen adresse (32 hex-tegn, åbnes fra
    // loggen). Samme side og samme grant som oversigten (længste prefix i
    // kataloget); samme komponent, der følger :uid.
    path: '/webshop/cro/pagespeed/:uid',
    name: 'webshop-cro-pagespeed-run',
    component: () => import('./views/webshop/CroPagespeedView.vue'),
    meta: { title: 'CRO · Pagespeed' },
  },
  {
    path: '/webshop/cro/pagespeed',
    name: 'webshop-cro-pagespeed',
    component: () => import('./views/webshop/CroPagespeedView.vue'),
    meta: { title: 'CRO · Pagespeed' },
  },

  // ---- Webshop → Relewise ([D-364]) ----
  {
    path: '/webshop/relewise',
    name: 'webshop-relewise',
    component: () => import('./views/webshop/RelewiseView.vue'),
    meta: { title: 'Relewise' },
  },

  // ---- Webshop → Feeds ([D-373]) ----
  {
    path: '/webshop/feeds',
    name: 'webshop-feeds',
    component: () => import('./views/webshop/FeedsView.vue'),
    meta: { title: 'Feeds' },
  },

  // ---- Webshop → Billeder ([D-327]) ----
  { path: '/webshop/images', redirect: '/webshop/images/review' },
  {
    path: '/webshop/images/review',
    name: 'webshop-images-review',
    component: () => import('./views/webshop/ImagesReviewView.vue'),
    meta: { title: 'Billeder · Til gennemgang' },
  },
  {
    path: '/webshop/images/products',
    name: 'webshop-images-products',
    component: () => import('./views/webshop/ImagesProductsView.vue'),
    meta: { title: 'Billeder · Produkter' },
  },
  {
    // Kategorier/Sider er oprettet grå (§32): kategori- og indholdsside-
    // billeder er senere faser i docs/17-produktbilleder-plan.md.
    path: '/webshop/images/categories',
    name: 'webshop-images-categories',
    component: () => import('./views/webshop/ImagesEmptyView.vue'),
    meta: { title: 'Billeder · Kategorier', empty: true },
  },
  {
    path: '/webshop/images/pages',
    name: 'webshop-images-pages',
    component: () => import('./views/webshop/ImagesEmptyView.vue'),
    meta: { title: 'Billeder · Sider', empty: true },
  },
  {
    path: '/webshop/images/rules',
    name: 'webshop-images-rules',
    component: () => import('./views/webshop/ImagesRulesView.vue'),
    meta: { title: 'Billeder · Regelsæt' },
  },
  {
    path: '/webshop/images/validation',
    name: 'webshop-images-validation',
    component: () => import('./views/webshop/ImagesValidationView.vue'),
    meta: { title: 'Billeder · Validering' },
  },

  // ---- Butikker ----
  {
    path: '/stores',
    name: 'stores',
    component: () => import('./views/StoresView.vue'),
    meta: { title: 'Åbningstider' },
  },
  {
    path: '/stores/monitors',
    name: 'stores-monitors',
    component: () => import('./views/InfoskaermeView.vue'),
    meta: { title: 'Infoskærme' },
  },
  // Salgsstatistik ([D-150], samlet til ÉN side 17-07): dimensionen
  // (leverandør/serie/type) vælges i filterpanelet, ikke i menuen.
  {
    path: '/stores/salesstats',
    name: 'stores-salesstats',
    component: () => import('./views/stores/SalgsstatistikView.vue'),
    meta: { title: 'Salgsstatistik' },
  },
  // Pipeline ([D-472]): tilbud og ordrer i kolonner med forventet omsætning.
  {
    path: '/stores/pipeline',
    name: 'stores-pipeline',
    component: () => import('./views/stores/PipelineView.vue'),
    meta: { title: 'Pipeline' },
  },
  // Prisskilte ([D-343]): Butikker → Prisskilte → Skilte / Regelsæt / Udstillingsmiljøer ([D-499]).
  { path: '/stores/price-tags', redirect: '/stores/price-tags/signs' },
  {
    path: '/stores/price-tags/signs',
    name: 'stores-price-tags-signs',
    component: () => import('./views/stores/PriceTagsView.vue'),
    meta: { title: 'Prisskilte · Skilte' },
  },
  {
    path: '/stores/price-tags/rules',
    name: 'stores-price-tags-rules',
    component: () => import('./views/stores/PriceTagRulesView.vue'),
    meta: { title: 'Prisskilte · Regelsæt' },
  },
  {
    path: '/stores/price-tags/showrooms',
    name: 'stores-price-tags-showrooms',
    component: () => import('./views/stores/PriceTagShowroomsView.vue'),
    meta: { title: 'Prisskilte · Udstillingsmiljøer' },
  },
  // Gamle underside-URL'er (bogmærker) → den samlede side.
  { path: '/stores/salesstats/vendors', redirect: '/stores/salesstats' },
  { path: '/stores/salesstats/series', redirect: '/stores/salesstats' },
  { path: '/stores/salesstats/types', redirect: '/stores/salesstats' },

  // ---- Kunder ----
  {
    path: '/customers',
    name: 'customers',
    component: placeholderView,
    meta: { title: 'Kunder', empty: true },
  },
  // Rabatgrupper ([D-169]-udvidelsen): rabatgruppe-vagtens kandidater +
  // ignorér-flow + kunder m. debitorrabatgruppe.
  {
    path: '/customers/discount-groups',
    name: 'customers-discount-groups',
    component: () => import('./views/customers/DiscountGroupsView.vue'),
    meta: { title: 'Rabatgrupper' },
  },
  // Kunderejsen ([D-417]): flowdiagram over touchpoints, indgange, genkøb
  // pr. Kompletsalg/Styksalg og CLV.
  {
    path: '/customers/journey',
    name: 'customers-journey',
    component: () => import('./views/customers/CustomerJourneyView.vue'),
    meta: { title: 'Kunderejsen' },
  },

  // ---- Varer ([D-222]) ----
  // Vare-siderne lå under Budget indtil 20-08-2026. De handler om husets
  // SORTIMENT, ikke om budgettet, og har derfor fået egen hovedmenu.
  {
    path: '/items/prices',
    name: 'items-prices',
    component: () => import('./views/PrisjusteringerView.vue'),
    // ⚠ Titlen er sidens navn i menuen OG i browserfanen: **Varepriser**.
    // Den stod som "Prisjusteringer" indtil [D-222] — et levn fra [D-121], hvor
    // viewet blev født. Menuen har sagt Varepriser siden [D-199].
    meta: { title: 'Varepriser' },
  },
  {
    path: '/items/discounts',
    name: 'items-discounts',
    component: () => import('./views/RabatstyringView.vue'),
    meta: { title: 'Rabatstyring' },
  },
  // Leverandører (M1 statisk UI-shell, feat/leverandorer-side): kontrolpanelet
  // til Kostpris- og Salgspris-agenterne. Kobler BC-leverandører til
  // SharePoint-mapper og styrer parser, fil-selektor, aktiv/auto pr.
  // leverandør. Backend bygges i M2-M5; siden er hardkodet mock i M1.
  // [D-336]: engelsk sti; /items/leverandorer viderestiller for altid (§21).
  // API'et hedder fortsat /api/items/leverandorer (§21c: kun siden flytter).
  {
    path: '/items/vendors',
    name: 'items-vendors',
    component: () => import('./views/items/LeverandorerView.vue'),
    meta: { title: 'Leverandører' },
  },
  { path: '/items/leverandorer', redirect: (to) => ({ path: '/items/vendors', query: to.query }) },
  // Log: agent-kørsler grupperet pr. correlation_id (Salgspris-agenten).
  // Genbruger system_log; én linje = én kørsel; klik = drawer med detaljer.
  {
    path: '/items/log',
    name: 'items-log',
    component: () => import('./views/items/AgentLogView.vue'),
    meta: { title: 'Log' },
  },
  // Prishistorik: søg vare frem, se tidslinje af alle prisændringer.
  {
    path: '/items/price-history',
    name: 'items-price-history',
    component: () => import('./views/items/PriceHistoryView.vue'),
    meta: { title: 'Prishistorik' },
  },
  // Vareopslag ([D-498], Charlie 30-10-2026): søg en vare frem og se hele
  // rapporten (BC og WP live, salg, tidslinje) med knappen der gennemtvinger
  // BC's pris i WP. Varen står i adressen (?item=).
  {
    path: '/items/lookup',
    name: 'items-lookup',
    component: () => import('./views/items/VareopslagView.vue'),
    meta: { title: 'Vareopslag' },
  },
  // Lagervarer ([D-300], Charlie 17-09-2026): alle lagervarer ifølge BC m.
  // valgfri periode uden salg og filtrene "kun disponibel beholdning" + "skjul
  // BF Lagervarer" (i url'en), grupperet pr. leverandør/produktserie/produkttype.
  {
    path: '/items/inventory',
    name: 'items-inventory',
    component: () => import('./views/items/LagervarerView.vue'),
    meta: { title: 'Lagervarer' },
  },
  // Siden hed Lagerudsalg på /items/clearance fra [D-291] (11-09-2026) til
  // [D-300] (17-09-2026). En slug i drift dør aldrig (§21): redirect for altid,
  // grants migreret m. migrate:page-slug-grants. Query'en (mdr, gruppe, q,
  // undtaget, regelsaet) følger med, så delte links lander på samme udsnit.
  { path: '/items/clearance', redirect: (to) => ({ path: '/items/inventory', query: to.query }) },

  // ---- Logistik ([D-222]) ----
  {
    path: '/logistics/shipping-prices',
    name: 'logistics-shipping-prices',
    component: () => import('./views/FragtpriserView.vue'),
    // Siden er oprettet, men modulet er ikke bygget endnu → §32: menupunktet
    // tegnes svagt, og sandheden om det står HER på ruten.
    meta: { title: 'Fragtpriser', empty: true },
  },

  // ---- Regnskab ([D-222], hed Budget indtil 20-08-2026) ----
  {
    // Siden hed Oversigt indtil [D-199] og Driftbudget indtil [D-222]. Nu hedder
    // sektionen Regnskab, og siden simpelthen Budget: navnet siger hvad siden ER,
    // og sektionen hvor den hører hjemme.
    path: '/accounting/budget',
    name: 'accounting-budget',
    component: () => import('./views/BudgetView.vue'),
    meta: { title: 'Budget' },
  },
  // Periodeafstemning ([D-184]): budgetlinjer uden bogført beløb i fuldt
  // bogførte måneder — listen bag vagt-banneret på Regnskab → Budget.
  {
    path: '/accounting/reconciliation',
    name: 'accounting-reconciliation',
    component: () => import('./views/PeriodeafstemningView.vue'),
    meta: { title: 'Periodeafstemning' },
  },
  {
    path: '/accounting/accounts',
    name: 'accounting-accounts',
    component: () => import('./views/AccountExplorerView.vue'),
    meta: { title: 'Konti & sektioner' },
  },
  // Udgiftsbilag ([D-391]): mails m. PDF-bilag i marketing@/charlie@ →
  // godkendelseskø + videresendte + undtagelsesregler.
  {
    path: '/accounting/expense-docs',
    name: 'accounting-expense-docs',
    component: () => import('./views/AccountingExpenseDocsView.vue'),
    meta: { title: 'Udgiftsbilag' },
  },
  // Index ([D-251]): husets nøgletal måned for måned, ét driftår pr. kolonne,
  // så sæsonudsvingene kan ses på tværs af årene. Tallene er regnskabets egne
  // (BC's kontoplan-intervaller), hentet tilbage til 2014.
  // [D-306] (25-09-2026): Index er en gruppe med 3 undersider (Charlies
  // bestilling: Regnskab → Index → Finansielt / Marked / Vækst). Den gamle sti
  // '/accounting/index' redirecter til Finansielt (§21), og grants migreres
  // med migrate:page-slug-grants FØR deploy (§21b). Marked og Vækst er nye
  // katalog-sider med egne grants (ikke navigation som kampagnernes faser:
  // de viser forskelligt indhold, og Marked har redigér-ret på indtastningen).
  { path: '/accounting/index', redirect: '/accounting/index/financial' },
  {
    path: '/accounting/index/financial',
    name: 'accounting-index-financial',
    component: () => import('./views/AccountingIndexView.vue'),
    meta: { title: 'Finansielt index' },
  },
  // Marked ([D-306]): leverandørernes egne markedsindex (indtastet) mod husets
  // index hos samme leverandør (målt på vareposteringerne i BC).
  {
    path: '/accounting/index/market',
    name: 'accounting-index-market',
    component: () => import('./views/AccountingIndexMarketView.vue'),
    meta: { title: 'Markedsindex' },
  },
  // Vækst ([D-306]): årlig nettovækst i omsætningen mod nettovækst i
  // annonceforbruget, driftår for driftår, og testen af 10 %-tesen.
  {
    path: '/accounting/index/growth',
    name: 'accounting-index-growth',
    component: () => import('./views/AccountingIndexGrowthView.vue'),
    meta: { title: 'Vækstindex' },
  },
  // Leverandører ([D-306] v1.2): husets varesalg pr. leverandør, ét driftår
  // pr. kolonne fra 2014, index år-til-år i cellerne (Finansielt-mønstret).
  {
    path: '/accounting/index/suppliers',
    name: 'accounting-index-suppliers',
    component: () => import('./views/AccountingIndexSuppliersView.vue'),
    meta: { title: 'Leverandørindex' },
  },

  // Pensionerede slugs → nye sider (12-conventions §21: en slug der har været i
  // drift beholder ALTID en redirect, også når den selv var en redirect, så en
  // kæde ikke knækker på midten).
  //   '/budget'                    var redirect → '/budget/driftbudget' ([D-199])
  //   '/budget/driftbudget'        var siden selv fra [D-199] til [D-222]
  // Begge peger nu DIREKTE på '/accounting/budget'.
  { path: '/budget', redirect: '/accounting/budget' },
  { path: '/budget/driftbudget', redirect: '/accounting/budget' },
  { path: '/budget/reconciliation', redirect: '/accounting/reconciliation' },
  { path: '/budget/accounts', redirect: '/accounting/accounts' },
  { path: '/budget/item-price-adj', redirect: '/items/prices' },
  { path: '/budget/discounts', redirect: '/items/discounts' },
  { path: '/budget/shipping-price-adj', redirect: '/logistics/shipping-prices' },

  // ---- Dokumentation ([D-131]: vidensbanken) ----
  {
    path: '/doc',
    redirect: '/doc/attribution/revenue',
  },
  {
    path: '/doc/attribution/revenue',
    name: 'doc-attribution-revenue',
    component: () => import('./views/docs/RevenueDocView.vue'),
    meta: { title: 'Dokumentation · Omsætning' },
  },
  {
    path: '/doc/security',
    name: 'doc-security',
    component: () => import('./views/docs/SecurityDocView.vue'),
    meta: { title: 'Dokumentation · Sikkerhedspolitik' },
  },
  {
    path: '/doc/glossary',
    name: 'doc-glossary',
    component: () => import('./views/docs/GlossaryDocView.vue'),
    meta: { title: 'Dokumentation · Ordbog' },
  },
  {
    path: '/doc/freshness',
    name: 'doc-freshness',
    component: () => import('./views/docs/FreshnessDocView.vue'),
    meta: { title: 'Dokumentation · Datafriskhed' },
  },
  {
    path: '/doc/agents',
    name: 'doc-agents',
    component: () => import('./views/docs/AgentsDocView.vue'),
    meta: { title: 'Dokumentation · Agenter' },
  },
  // SEO ([D-226]): hvordan appen udfører SEO — teknik, regelsæt og brand-lag.
  {
    path: '/doc/seo',
    name: 'doc-seo',
    component: () => import('./views/docs/SeoDocView.vue'),
    meta: { title: 'Dokumentation · SEO' },
  },
  // Bruttofortjeneste og avance ([D-449]): kostbeløb fra BC's værdiposter,
  // varemargin mod regnskabets dækningsgrad, Rabatstyrings vinduer ([D-446]).
  {
    path: '/doc/gross-profit',
    name: 'doc-gross-profit',
    component: () => import('./views/docs/BruttofortjenesteDocView.vue'),
    meta: { title: 'Dokumentation · Bruttofortjeneste og avance' },
  },
  {
    path: '/doc/budget',
    name: 'doc-budget',
    component: () => import('./views/docs/BudgetDocView.vue'),
    meta: { title: 'Dokumentation · Budget' },
  },
  // [D-336]: engelske stier; de danske viderestiller for altid (§21).
  {
    path: '/doc/phone',
    name: 'doc-phone',
    component: () => import('./views/docs/TelefonNaevnerDocView.vue'),
    meta: { title: 'Dokumentation · Telefon-nævner' },
  },
  { path: '/doc/telefon-naevner', redirect: (to) => ({ path: '/doc/phone', query: to.query }) },
  {
    path: '/doc/store-visits',
    name: 'doc-store-visits',
    component: () => import('./views/docs/ButiksbesoegDocView.vue'),
    meta: { title: 'Dokumentation · Butiksbesøg' },
  },
  { path: '/doc/butiksbesoeg', redirect: (to) => ({ path: '/doc/store-visits', query: to.query }) },
  // Customer Match ([D-285]): hvad der sendes til Google Ads og Meta, hvorfor,
  // og hvad der aldrig sendes.
  {
    path: '/doc/customer-match',
    name: 'doc-customer-match',
    component: () => import('./views/docs/CustomerMatchDocView.vue'),
    meta: { title: 'Dokumentation · Customer Match' },
  },

  // ---- Driftstatus ([D-222], lå under /settings indtil 20-08-2026) ----
  // Siderne blev taget ud af Indstillinger-MENUEN 18-08-2026 og bor i
  // sidebarens bund-gruppe. Stien fulgte efter her, så URL og menu siger det
  // samme. Bemærk at de 2 sider IKKE deler prefix ('/ops/jobs' er ikke et
  // prefix af '/ops/log'), hvilket fjerner den aktiv-markerings- og
  // grant-opslags-fælde de gamle stier havde.
  {
    path: '/ops/jobs',
    name: 'ops-jobs',
    component: () => import('./views/settings/OpsStatusView.vue'),
    meta: { title: 'Kørsler', showHeader: false },
  },
  // Systemlog ([D-202]): al datatrafik til og fra tredjeparter, kronologisk.
  // Egen side under Driftstatus, så adgangen kan gives alene.
  {
    path: '/ops/log',
    name: 'ops-log',
    component: () => import('./views/settings/SystemLogView.vue'),
    // ⚠ showHeader UDELADT (= true) i modsætning til indstillings-siderne:
    // siden er en datavisning med eksport-handlinger, og PDF/Excel-knapperne
    // hører til i shell-headeren efter husets konvention. Uden headeren findes
    // #filter-shell-actions slet ikke, og en Teleport dertil VÆLTER
    // renderingen af resten af siden.
    meta: { title: 'Log' },
  },
  // Platformvagten ([D-402]): fund fra platform-varsler, godkendelseskø og
  // historik. showHeader false som Kørsler (ingen teleports til shell-headeren).
  {
    path: '/ops/actions',
    name: 'ops-actions',
    component: () => import('./views/settings/OpsActionsView.vue'),
    meta: { title: 'Handlinger', showHeader: false },
  },
  // Driftsforstyrrelser ([D-418]): Driftsvagtens fund + retterens resultat.
  {
    path: '/ops/disruptions',
    name: 'ops-disruptions',
    component: () => import('./views/settings/OpsDisruptionsView.vue'),
    meta: { title: 'Driftsforstyrrelser', showHeader: false },
  },
  // Pensionerede slugs (§21).
  { path: '/settings/ops', redirect: '/ops/jobs' },
  { path: '/settings/ops/log', redirect: '/ops/log' },

  // ---- Indstillinger ----
  {
    path: '/settings',
    component: () => import('./views/SettingsView.vue'),
    meta: { title: 'Indstillinger' },
    children: [
      {
        path: '',
        redirect: '/settings/users',
      },
      {
        path: 'users',
        name: 'settings-users',
        component: () => import('./views/settings/BrugereView.vue'),
        meta: { title: 'Brugere', showHeader: false },
      },
      // [D-141]: grants redigeres pr. bruger her (Rettigheder-siden er udgået).
      // Router-guardens canAccessPath prefix-matcher /settings/users/:id mod
      // katalog-siden /settings/users. Sticky-headeren vises (Teleport-titel
      // med brugerens navn), derfor INTET showHeader: false.
      {
        path: 'users/:id',
        name: 'settings-user-detail',
        component: () => import('./views/settings/UserDetailView.vue'),
        meta: { title: 'Bruger' },
      },
      // Siderettigheder ([D-178]): side-centreret grant-administration —
      // hvilke brugere har adgang til hvilke sider + skabelon-redigering.
      {
        path: 'page-permissions',
        name: 'settings-page-permissions',
        component: () => import('./views/settings/PagePermissionsView.vue'),
        meta: { title: 'Siderettigheder', showHeader: false },
      },
      {
        path: 'api-keys',
        name: 'settings-api-keys',
        component: () => import('./views/settings/ApiKeysView.vue'),
        meta: { title: 'API-nøgler', showHeader: false },
      },
      // Sprogmodeller ([D-234]): model pr. LLM-opgave, succesrater + skifte-log.
      {
        path: 'llm',
        name: 'settings-llm',
        component: () => import('./views/settings/SprogmodellerView.vue'),
        meta: { title: 'Sprogmodeller', showHeader: false },
      },
      // Min profil ([D-155]): BEVIDST uden katalog-side — åben for alle
      // loggede ind (tema-valget bor her, flyttet fra hovedmenuen).
      {
        path: 'profile',
        name: 'settings-profile',
        component: () => import('./views/settings/ProfileView.vue'),
        meta: { title: 'Min profil', showHeader: false },
      },
      // Driftstatus er FLYTTET til top-level '/ops' ([D-222]) — menuen tog den
      // ud af Indstillinger allerede 18-08-2026, og nu følger stien efter.
      // De 2 gamle stier står som redirects nederst i filen (§21).
      // Notifikationer ([D-169]): hvem modtager Cockpits automatiske mails.
      {
        path: 'notifications',
        name: 'settings-notifications',
        component: () => import('./views/settings/NotificationsView.vue'),
        meta: { title: 'Notifikationer', showHeader: false },
      },
      // Sideindstillinger (rev 060): samlet oversigt over tandhjuls-
      // indstillingerne fra de enkelte sider.
      {
        path: 'page-settings',
        name: 'settings-page-settings',
        component: () => import('./views/settings/PageSettingsView.vue'),
        meta: { title: 'Sideindstillinger', showHeader: false },
      },
    ],
  },
];

// Boot-værn ([D-304]): kampagnernes 5 undermenuer står skrevet ud ovenfor, men
// venstremenuen, fanerne og kampagnesidens tilbage-link bygges af CAMPAIGN_MENU
// i shared. Mangler en rute, ville menupunktet føre til en tom side uden fejl.
for (const m of CAMPAIGN_MENU) {
  const path = `${CAMPAIGNS_BASE_PATH}/${m.slug}`;
  const hit = routes.find((r) => r.path === path);
  if (!hit || hit.meta?.campaignLifecycle !== m.lifecycle) {
    throw new Error(`router: kampagne-undermenuen '${path}' mangler eller peger på en anden fase end '${m.lifecycle}'`);
  }
}

export const router = createRouter({
  history: createWebHashHistory(),
  routes,
});
// [D-480]: før alle andre guards, så adressens kø (patchRouteQuery) ser et klik væk fra siden med det samme.
watchLeaving(router);

/**
 * Selvhelende chunk-fejl (Charlies "kan ikke klikke"-fund 06-08-2026): efter
 * et deploy findes den gamle fanes hashede chunks ikke længere på serveren,
 * så lazy-import af en side fejler LYDLØST — klik i menuen "gør ingenting"
 * indtil man hard-refresher. Her fanges fejlen og siden genindlæses fuldt
 * (nyt bundle) direkte på målruten. Loop-værn: højst én auto-reload pr. 15 s
 * (skulle det nye bundle også fejle, ender vi ikke i evig reload).
 */
const RELOAD_GUARD_KEY = 'cockpit:chunk-reload-at';
function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as { message?: string })?.message ?? err ?? '');
  // ⚠ "Failed to fetch" er BROWSERENS fejltekst, ikke husets — et sprog-sweep
  //   skrev den engang om til "Failed 2 fetch" (§11-cifferreglen gælder KUN
  //   menneskerettede tekster), og Chrome/Edge-fejl matchede aldrig igen.
  //   Fanget 26-09-2026 under Charlies "første klik loader samme side"-fund.
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError/i.test(
    msg,
  );
}
function reloadOnStaleBundle(targetFullPath?: string): void {
  const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0');
  if (Date.now() - last < 15_000) return;
  sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  if (targetFullPath) window.location.hash = `#${targetFullPath}`;
  window.location.reload();
}
/**
 * Målet for den navigation der er I GANG. Vites preload-fejl affyres FØR
 * navigationen er gennemført, så location.hash peger stadig på den GAMLE
 * side i det øjeblik — en reload uden mål landede derfor på siden man stod
 * på, og klikket lignede et der "ikke virkede" før man klikkede igen
 * (Charlies fund 26-09-2026: første klik loader samme side, andet klik den
 * rigtige). Sættes i beforeEach og ryddes når navigationen er afgjort.
 */
let pendingNavTarget: string | null = null;
router.beforeEach((to) => {
  pendingNavTarget = to.fullPath;
});
router.afterEach(() => {
  pendingNavTarget = null;
});
router.onError((error, to) => {
  if (isChunkLoadError(error)) reloadOnStaleBundle(to?.fullPath ?? pendingNavTarget ?? undefined);
  pendingNavTarget = null;
});
// Vites preload-fejl (CSS/asset-preloads for lazy chunks) rammer uden om
// routerens onError — samme selvhelings-reload, på det VENTENDE mål.
window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault();
  reloadOnStaleBundle(pendingNavTarget ?? undefined);
});

router.beforeEach((to) => {
  const meta = to.meta.title;
  document.title = typeof meta === 'string' ? `${meta}, Cockpit` : 'Cockpit';
  // Åbn et side-load-vindue ([D-134]): den gennemgående footer-bar vises hvis
  // siden har et GET /api-kald in-flight efter vis-forsinkelsen (generelt for
  // alle sider; lette sider flasher ikke).
  startPageLoad();
});

// Auth-gate: public-ruter (login) er altid tilgængelige; alt andet kræver session.
// Afventer session-hydreringen (F5) så guarden aldrig evaluerer auth.user/roller
// før /api/auth/me + /permissions er hentet. Løftet er cachet → efter første
// navigation er ventet nul.
router.beforeEach(async (to) => {
  await ensureSessionLoaded();
  // [D-420]: tilbage til monitoren efter et nyt login (SSO lander altid på /).
  if (auth.user) {
    const back = consumeReturnAfterLogin();
    if (back && back !== to.fullPath) return back;
  }
  // [D-420]: monitor-visningen gater sig selv (eget login på 90 dage, serveren
  // afgør adgangen pr. kald), så den må tegnes uden app-session.
  if (to.meta.monitor === true) return true;
  if (to.meta.public === true) {
    // Allerede logget ind på login-siden → send til brugerens første tilladte
    // side (IKKE hårdkodet /marketing — en bruger uden /marketing-grant ville
    // ellers ryge i redirect-loop). Ingen tilladt side → /no-access.
    if (auth.user && to.name === 'login') {
      const roles = (auth.user.roles ?? []) as RoleCode[];
      return { path: firstAllowedPath(roles) ?? '/no-access' };
    }
    return true;
  }
  if (!auth.user) return { name: 'login' };

  // Retry af side-kataloget hvis app-start-hentningen fejlede ([D-141]-fix #20):
  // ellers ville fallback-kataloget (grant-nøgler) styre resten af sessionen.
  if (permissionsState.loaded && permissionsState.pages.length === 0) {
    await fetchPermissions();
  }

  // Permission-gate ([D-141]): afvist adgang → send til brugerens FØRSTE tilladte
  // side; har brugeren ingen sider → /no-access. Loop-værn (#8/#15): redirect
  // ALDRIG til to.path selv (/no-access er public og gates aldrig).
  const roles = (auth.user.roles ?? []) as RoleCode[];
  if (!canAccessPath(to.path, roles)) {
    const target = firstAllowedPath(roles);
    if (target && target !== to.path) return { path: target };
    return { path: '/no-access' };
  }
  return true;
});
